import { Component, Prop, h, Host, Build } from '@stencil/core';

const COMPONENT_NAME = 'sn-call-to-action-type-7';

/**
 * Dark-navy CTA banner with heading and description on the left and an action button on the right (desktop).
 * On mobile the button appears below the description.
 * Dispatches the `sn-form-loader-open` custom event when the action button is clicked.
 *
 * @product signnow
 * @version 2.0.0
 * @category SignNow
 * @contentConfig Call-to-action banner with heading, description and action button for encouraging users to create forms
 * @example
 *   <sn-call-to-action-type-7 form-id="my-form-id">
 *     <me-heading slot="heading" variant="2" tag="h2">Create this form in 5 minutes!</me-heading>
 *     <me-text slot="description" tag="p">Use professional pre-built templates to fill in and sign documents online faster. Get access to thousands of forms.</me-text>
 *     <me-button slot="action-primary" variant="primary" tag="button">Get Form</me-button>
 *   </sn-call-to-action-type-7>
 * @slot heading - {required}, <me-heading variant="2" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Main CTA heading
 * @slot description - <me-text tag="p"> - Description text below the heading
 * @slot action-primary - {required}, <me-button variant="primary"> - Action button (triggers form loader)
 */
@Component({
  tag: 'sn-call-to-action-type-7',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNCallToActionType7 {
  /**
   * Color mode for theming
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Unique form identifier passed as detail to the `sn-form-loader-open` event
   */
  @Prop({ attribute: 'form-id', reflect: true }) formId?: string;

  private handleCtaClick = (event: Event) => {
    event.preventDefault();
    if (!Build.isBrowser) return;
    const formId = this.formId || (document.querySelector('[data-formid]') as HTMLElement)?.dataset?.formid;
    window.dispatchEvent(new CustomEvent('sn-form-loader-open', { detail: formId }));
  };

  render() {
    return (
      <Host data-color-mode={this.colorMode}>
        <section class={COMPONENT_NAME}>
          <div class={`${COMPONENT_NAME}__container`}>
            <div class={`${COMPONENT_NAME}__inner`}>
              <div class={`${COMPONENT_NAME}__content`}>
                <slot name="heading"></slot>
                <slot name="description"></slot>
              </div>
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div class={`${COMPONENT_NAME}__action js-editor-link`} onClick={this.handleCtaClick}>
                <slot name="action-primary"></slot>
              </div>
            </div>
          </div>
        </section>
      </Host>
    );
  }
}
